/**
 * GetAppCard — Round 44 (Sep 30 2026).
 *
 * The website now leads with the mobile app; the web app stays for anyone who
 * prefers a browser. Inside the web app this small card says so once:
 * "For the best experience, download the GEMS app", with a button to /get-app
 * (which picks the right store for the device) and a close button.
 *
 * Never shown:
 *   • to someone who arrived from the app itself (the app opens the web
 *     wallet with a one-time #session_id — telling them to "get the app"
 *     would be silly),
 *   • inside the app's in-page views (legal pages open in its WebView),
 *   • for 14 days after it was closed.
 */
import React, { useEffect, useState } from 'react';

const DISMISS_KEY = 'gems_get_app_card_closed_at';
const FROM_APP_KEY = 'gems_opened_from_app';
const QUIET_DAYS = 14;

const read = (store, key) => { try { return store.getItem(key); } catch (e) { return null; } };
const write = (store, key, value) => { try { store.setItem(key, value); } catch (e) { /* private mode: the card just comes back */ } };

const cameFromApp = () => {
  if (typeof window === 'undefined') return true;
  if ((window.location.hash || '').includes('session_id=')) {
    write(window.sessionStorage, FROM_APP_KEY, '1');
    return true;
  }
  if (read(window.sessionStorage, FROM_APP_KEY) === '1') return true;
  return !!window.ReactNativeWebView;
};

const recentlyClosed = () => {
  const at = parseInt(read(window.localStorage, DISMISS_KEY) || '0', 10);
  return at > 0 && Date.now() - at < QUIET_DAYS * 24 * 60 * 60 * 1000;
};

const GetAppCard = () => {
  const [show, setShow] = useState(false);
  // read during the first render: the web app strips the #session_id a moment later
  const [fromApp] = useState(cameFromApp);

  useEffect(() => {
    if (fromApp || recentlyClosed()) return undefined;
    const t = setTimeout(() => setShow(true), 2500);   // after the page has settled
    return () => clearTimeout(t);
  }, [fromApp]);

  if (!show) return null;

  const close = () => { write(window.localStorage, DISMISS_KEY, String(Date.now())); setShow(false); };

  return (
    <div
      role="complementary"
      aria-label="Get the GEMS app"
      data-testid="get-app-card"
      style={{
        position: 'fixed', right: 16, bottom: 88, zIndex: 9000, maxWidth: 320, width: 'calc(100vw - 32px)',
        background: '#0f1a15', color: '#f4fbf7', border: '1px solid rgba(62,230,164,0.35)', borderRadius: 16,
        boxShadow: '0 12px 32px rgba(0,0,0,0.45)', padding: '14px 14px 14px 16px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <button
        onClick={close}
        aria-label="Close"
        data-testid="get-app-card-close"
        style={{ position: 'absolute', top: 6, right: 8, background: 'transparent', border: 0, color: '#9db3a8', fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: 6 }}
      >
        ×
      </button>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, paddingRight: 24 }}>GEMS is best on the app</div>
      <div style={{ fontSize: 13, color: '#b7c9bf', lineHeight: 1.45, marginBottom: 12 }}>
        For the best experience, download the GEMS mobile app. You can keep using the web any time.
      </div>
      <a
        href="/get-app"
        data-testid="get-app-card-cta"
        style={{ display: 'inline-block', background: '#10b981', color: '#04120c', fontWeight: 700, fontSize: 14, borderRadius: 999, padding: '9px 18px', textDecoration: 'none' }}
      >
        Get the app
      </a>
    </div>
  );
};

export default GetAppCard;
